// apps/web/src/pages/vendas/detalhe/mais-acoes-menu.tsx — Fase 12, Plano 11 (GOV-02, D-39; UI-SPEC § Cabeçalho).
// Menu "Mais ações" do cabeçalho do PV, aberto por clique/toque (nunca só por hover). As ações chegam por props:
// cada plano acrescenta as suas (12-31: editar cabeçalho, encerrar saldo e cancelar PV) e a página decide a permissão.
import { MoreHorizontal } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';

export interface AcaoMaisAcoesPv {
  /** Identificador estável (chave React e seletor de teste). */
  readonly id: string;
  readonly rotulo: string;
  readonly onSelecionar: () => void;
}

interface MaisAcoesMenuProps {
  readonly numeroPv: string;
  readonly acoes: readonly AcaoMaisAcoesPv[];
}

export function MaisAcoesMenu({ numeroPv, acoes }: MaisAcoesMenuProps) {
  if (acoes.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="icon" aria-label={`Mais ações do PV ${numeroPv}`} className="size-11 sm:size-9">
          <MoreHorizontal aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {acoes.map((acao) => (
          <DropdownMenuItem key={acao.id} data-acao={acao.id} onSelect={acao.onSelecionar} className="min-h-10">
            {acao.rotulo}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Corpo do diálogo destrutivo "Cancelar PV" (UI-SPEC § Cancelar PV). */
export function corpoCancelarPv(numeroPv: string): string {
  return `Cancelar o PV ${numeroPv} cancela todos os itens ainda não recebidos. Itens já recebidos exigem ajuste antes. O motivo fica registrado na auditoria. Confirmar cancelamento?`;
}

interface ContextoAcoesMaisAcoes {
  readonly podeEditar: boolean;
  readonly podeAjustar: boolean;
  /** PV cancelado: não há mais o que cancelar nem saldo a encerrar. */
  readonly pvCancelado: boolean;
  /** Existe ao menos um item ativo com saldo a encerrar. */
  readonly temItemComSaldo: boolean;
  readonly onEditarCabecalho: () => void;
  readonly onVincularExistente: () => void;
  readonly onEncerrarSaldo: () => void;
  readonly onCancelarPv: () => void;
  /** 12-17: abre o relatório de acompanhamento com o cliente do PV travado (qualquer usuário com pv:ler). */
  readonly onGerarRelatorio?: () => void;
}

/** Ações do menu do PV, na ordem do UI-SPEC e filtradas pela permissão (a autorização real está no servidor). */
export function montarAcoesMaisAcoes(contexto: ContextoAcoesMaisAcoes): AcaoMaisAcoesPv[] {
  const acoes: AcaoMaisAcoesPv[] = [];
  // PV cancelado não aceita mais mudanças (a API responde 409): só ficam as ações de leitura.
  if (contexto.podeEditar && !contexto.pvCancelado) {
    acoes.push({ id: 'editar-cabecalho', rotulo: 'Editar cabeçalho', onSelecionar: contexto.onEditarCabecalho });
  }
  if (contexto.podeAjustar && !contexto.pvCancelado) {
    acoes.push({ id: 'vincular-existente', rotulo: 'Vincular lote ou remessa existente', onSelecionar: contexto.onVincularExistente });
  }
  if (contexto.podeAjustar && !contexto.pvCancelado && contexto.temItemComSaldo) {
    acoes.push({ id: 'encerrar-saldo', rotulo: 'Encerrar saldo', onSelecionar: contexto.onEncerrarSaldo });
  }
  if (contexto.onGerarRelatorio) {
    acoes.push({ id: 'gerar-relatorio', rotulo: 'Gerar relatório', onSelecionar: contexto.onGerarRelatorio });
  }
  if (contexto.podeEditar && !contexto.pvCancelado) {
    acoes.push({ id: 'cancelar-pv', rotulo: 'Cancelar PV', onSelecionar: contexto.onCancelarPv });
  }
  return acoes;
}
